"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const filterTopics = [{
  label: "Budgets & Costs",
  href: "#budgets"
}, {
  label: "Permits & Codes",
  href: "#permits"
}, {
  label: "Kitchen Design",
  href: "#kitchens"
}, {
  label: "Home Additions",
  href: "#additions"
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const fadeInUpVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: easeCurve
      }
    }
  };
  const plinthVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeCurve
      }
    }
  };
  return <section id="blog-intro" data-nav="light" className="relative bg-background text-foreground overflow-hidden py-16 md:py-24 lg:py-28 border-b border-border/60">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left Column: 5 Cols on LG */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={fadeInUpVariants} className="lg:col-span-5 flex flex-col justify-center space-y-6">
            {/* Eyebrow Badge */}
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary-foreground font-sans font-semibold text-xs tracking-wider uppercase px-3 py-1 rounded-full flex items-center gap-1.5">
                <Sparkles className="size-3 text-primary shrink-0" />
                Brookhaven Journal
              </Badge>
            </div>

            {/* Exact H1 Title */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.15] text-foreground">
              Remodeling Insights for Brookhaven Homeowners
            </h1>

            {/* Authoritative Lead Copy */}
            <p className="font-sans text-base sm:text-lg text-foreground/85 leading-relaxed">
              Explore practical guidance on renovation budgets, architectural planning, premium materials, and local municipal permitting throughout Brookhaven, Buckhead, and North Atlanta.
            </p>

            {/* Subtle Topic Filter Pill Bar */}
            <div className="pt-2 flex flex-col gap-2.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Jump to topics
              </span>
              <div className="flex flex-wrap gap-2">
                {filterTopics.map((topic, i) => <Link key={topic.label} to={topic.href} data-index={i} className="inline-flex items-center text-xs font-medium px-3.5 py-1.5 rounded-full border border-border bg-card text-foreground hover:border-primary/60 hover:text-foreground transition-[color,border-color,box-shadow] duration-200 hover:shadow-xs">
                    {topic.label}
                  </Link>)}
              </div>
            </div>
          </motion.div>

          {/* Right Column: 7 Cols on LG */}
          <div className="lg:col-span-7 relative">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeInUpVariants} className="relative rounded-2xl overflow-hidden shadow-xl border border-border/80 aspect-4/3 sm:aspect-16/10 lg:aspect-16/11 bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovated_kitchen_interior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovated_kitchen_interior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_renovated_kitchen_interior.png-wvc-sizes" alt="Sunlit Brookhaven luxury kitchen with honed quartzite counters and custom white oak cabinetry" loading="lazy" decoding="async" className="w-full h-full object-cover object-center ring-1 ring-inset ring-black/5" />

              {/* Atelier Frosted Glass Plinth Card */}
              <div className="absolute inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md pointer-events-auto">
                <motion.div variants={plinthVariants} animate={shouldReduceMotion ? undefined : {
                y: [0, -6, 0]
              }} transition={shouldReduceMotion ? undefined : {
                duration: 6,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }} className="rounded-2xl border border-white/80 bg-white/75 backdrop-blur-md p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="flex items-center justify-between gap-3 mb-2.5">
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-[11px] font-semibold tracking-wider uppercase px-2.5 py-0.5 rounded-full">
                      Featured Guide
                    </Badge>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                      <Clock className="size-3.5 text-primary" />
                      <span>7 min read</span>
                    </div>
                  </div>

                  <h2 className="font-serif text-lg sm:text-xl font-medium text-foreground tracking-tight line-clamp-2 leading-snug">
                    Navigating Brookhaven Residential Permitting & HOA Guidelines
                  </h2>

                  <p className="mt-1.5 text-xs sm:text-sm text-foreground/80 line-clamp-2 leading-relaxed">
                    A comprehensive walkthrough of zoning approvals, historic overlays, and architectural variance timelines.
                  </p>

                  <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <BookOpen className="size-3.5 text-primary" />
                      <span>Updated for 2025</span>
                    </div>

                    <Button asChild size="sm" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-xs tracking-wide px-4 py-2 transition-[background-color,box-shadow] duration-200 shadow-xs">
                      <Link to="/ContactRequestAnEstimatePage">
                        <span>Read guide</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}